import { planFor, recipeById, settleChoices, type PlanStep, type Recipe } from "./recipes";

/**
 * A project is a recipe in progress: one pattern, drafted for one body,
 * with its design chosen, and the steps ticked off as they are done.
 */

/**
 * One fitting check on a toile: fine as it is, or a change to make to the
 * draft, with how much each pattern measurement moves, in mm.
 */
export interface FitCheck { state: "ok" | "change"; note?: string; adjust?: Record<string, number> }

export interface ToileRound { checks: Record<string, FitCheck> }

export interface Project<Body = unknown> {
  id: string;
  recipeId: string;
  name: string;
  /** Who it is made for, and the body drafted for, kept as it was when the project began. */
  madeFor: string;
  body: Body;
  choices: Record<string, string>;
  toile: boolean;
  /** One per toile made; a new round is added when a toile needs another go. */
  toileRounds: ToileRound[];
  done: string[];
  created: number;
  updated: number;
}

const projectsStorageKey = "tailor-frame:projects";

export function loadProjects(): Project[] {
  try {
    const saved = JSON.parse(localStorage.getItem(projectsStorageKey) ?? "[]") as Project[];
    // A project whose recipe has gone is dropped; the rest keep only choices their recipe still offers.
    return saved.flatMap((project) => { const recipe = recipeById(project.recipeId); return recipe ? [{ ...project, choices: settleChoices(recipe, project.choices) }] : []; });
  } catch { return []; }
}

export function saveProjects(projects: Project[]) {
  try { localStorage.setItem(projectsStorageKey, JSON.stringify(projects)); } catch { /* storage unavailable: keep in memory only */ }
}

export function newProject<Body>(recipe: Recipe, madeFor: string, body: Body, choices: Record<string, string>, toile: boolean): Project<Body> {
  const now = Date.now();
  return { id: now.toString(36), recipeId: recipe.id, name: `${recipe.name} for ${madeFor}`, madeFor, body, choices: settleChoices(recipe, choices), toile, toileRounds: [{ checks: {} }], done: [], created: now, updated: now };
}

export function projectPlan(project: Project): PlanStep[] {
  const recipe = recipeById(project.recipeId);
  return recipe ? planFor(recipe, project.choices, { on: project.toile, rounds: project.toileRounds.length }) : [];
}

/** The first step not yet done, which the project carries on from. */
export const nextStep = (project: Project) => projectPlan(project).find((step) => !project.done.includes(step.id));

export function progressOf(project: Project) {
  const plan = projectPlan(project);
  const done = plan.filter((step) => project.done.includes(step.id)).length;
  return { done, total: plan.length, fraction: plan.length ? done / plan.length : 0 };
}

/** A design slot locks once any of its steps is done, so ticked work keeps matching the design. */
export const slotLocked = (project: Project, slotId: string) => projectPlan(project).some((step) => step.slot === slotId && project.done.includes(step.id));

/** The toile can be turned off only before any of it is done. */
export const toileLocked = (project: Project) => project.done.some((id) => id.startsWith("toile-"));

/** Every check of the round has an answer, so the fitting is done. */
export const fittingAnswered = (recipe: Recipe, round: ToileRound) => recipe.fitChecks.every((check) => round.checks[check.label] !== undefined);

/**
 * What the toiles changed, by pattern measurement, in mm: every round's
 * changes added up, since each toile is cut from the draft the last one left.
 */
export function draftAdjustments(project: Project): Record<string, number> {
  const total: Record<string, number> = {};
  for (const round of project.toileRounds) for (const check of Object.values(round.checks)) {
    if (check.state !== "change") continue;
    for (const [id, amount] of Object.entries(check.adjust ?? {})) total[id] = (total[id] ?? 0) + amount;
  }
  for (const [id, amount] of Object.entries(total)) if (amount === 0) delete total[id];
  return total;
}
